import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { utils, writeFile, read } from 'xlsx';
import {
  Modal,
  Button,
  Text,
  Table,
  Container,
  Spacer,
  Card,
  Row,
} from '@nextui-org/react';

interface BulkImportProps {
  requiredColumns?: string[];
  onSave?: (data: any[]) => void;
  templateFileName?: string;
}

const BulkImport: React.FC<BulkImportProps> = ({
  requiredColumns = ['Name', 'Email'],
  onSave,
  templateFileName = 'import_template.xlsx',
}) => {
  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    const file = acceptedFiles[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result;
      if (arrayBuffer) {
        try {
          const workbook = read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = utils.sheet_to_json(sheet, { header: 1 });

          if (jsonData.length === 0) {
            setError('File is empty');
            return;
          }

          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1);

          // Check for required columns
          const missingColumns = requiredColumns.filter(
            (col) => !headers.includes(col)
          );

          if (missingColumns.length > 0) {
            setError(
              `Missing required columns: ${missingColumns.join(', ')}`
            );
            return;
          }

          const formattedData = rows.map((row: any) => {
            const rowData: any = {};
            headers.forEach((header, index) => {
              rowData[header] = row[index];
            });
            return rowData;
          });

          setColumns(headers);
          setData(formattedData);
          setModalVisible(true);
        } catch (err) {
          setError('Failed to parse file');
          console.error(err);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  }, [requiredColumns]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const downloadTemplate = () => {
    const ws = utils.json_to_sheet([{}], { header: requiredColumns });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Template');
    writeFile(wb, templateFileName);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(data);
    }
    setModalVisible(false);
    // Reset or keep data? Let's reset for now or keep it as feedback.
    // setFileName(null);
    // setData([]);
  };

  const closeHandler = () => {
    setModalVisible(false);
    setFileName(null);
    setData([]);
  };

  return (
    <Container>
      <Card variant="bordered">
        <Card.Body>
          <div
            {...getRootProps()}
            style={{
              border: '2px dashed #ccc',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f0f0f0' : '#fff',
            }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Text>Drop the file here ...</Text>
            ) : (
              <Text>
                Drag 'n' drop some files here, or click to select files (.xlsx, .csv)
              </Text>
            )}
          </div>
        </Card.Body>
      </Card>

      <Spacer y={1} />

      <Row justify="center">
        <Button size="sm" onClick={downloadTemplate} ghost>
          Download Template
        </Button>
      </Row>

      {error && (
        <>
          <Spacer y={1} />
          <Text color="error" style={{ textAlign: 'center' }}>{error}</Text>
        </>
      )}

      <Modal
        closeButton
        aria-labelledby="modal-title"
        open={modalVisible}
        onClose={closeHandler}
        width="800px"
      >
        <Modal.Header>
          <Text id="modal-title" size={18}>
            Preview Import Data
          </Text>
        </Modal.Header>
        <Modal.Body>
          <Text>File: {fileName}</Text>
          <Text size={14} color="gray">Found {data.length} rows.</Text>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table
              aria-label="Import Preview Table"
              css={{
                height: "auto",
                minWidth: "100%",
              }}
            >
              <Table.Header>
                {columns.map((col) => (
                  <Table.Column key={col}>{col}</Table.Column>
                ))}
              </Table.Header>
              <Table.Body>
                {data.map((row, i) => (
                  <Table.Row key={i}>
                    {columns.map((col) => (
                      <Table.Cell key={`${i}-${col}`}>{row[col]}</Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button auto flat color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button auto onClick={handleSave}>
            Confirm Import
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BulkImport;
